import { Loader } from 'components/UI/Loader';
import { Layout } from 'features/auth/Layout';
import { useGetUserQuery } from 'features/data/useGetUserQuery';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import { useSelector } from 'hooks/utils';
import { Navigate } from 'react-router-dom';

const MAX_RETRIES = 9;
const MAX_RETRY_DELAY_IN_MS = 45 * 1000;

export function AccountStatusPage() {
	const userQuery = useGetUserQuery({
		retry: MAX_RETRIES,
		retryDelay: () => {
			return MAX_RETRY_DELAY_IN_MS / MAX_RETRIES;
		}
	});

	const language = useSelector(state => state.ui.i18n.language);
	const dictionary = DICTIONARY[language];

	return (
		<div>
			{userQuery.data?.user && <Navigate to={ROUTE_MAP.projects.path} />}

			{userQuery.isLoading && <LoaderComponent translation={dictionary} />}

			{userQuery.isError && <Error translation={dictionary} />}
		</div>
	);
}

const LoaderComponent = ({ translation }: { translation: Translation }) => (
	<Layout title={translation.loader.title}>
		<div className="flex flex-col text-base text-primary-700 rounded-sm p-6 bg-primary-500/20 gap-6 items-center">
			<p className="font-semibold">{translation.loader.firstParagraph}</p>

			<Loader color={c => c.primary} />
		</div>
	</Layout>
);

const Error = ({ translation }: { translation: Translation }) => (
	<Layout title={translation.error.title}>
		<div className="flex flex-wrap text-base text-error-700 rounded-sm p-6 bg-error-500/20">
			<p>{translation.error.firstParagraph}&nbsp;</p>

			<div className="mt-6 inline-block break-words">
				<span className="">{translation.error.secondParagraph}&nbsp;</span>

				<a href="mailto:support@ledidi.no" className="underline">
					support@ledidi.no
				</a>

				<span>&nbsp;{translation.error.thirdParagraph}</span>
			</div>
		</div>
	</Layout>
);

const DICTIONARY = {
	EN: {
		loader: {
			title: 'Initialising account',
			firstParagraph: 'Please wait while your account is being initialised'
		},

		error: {
			title: 'Error fetching account',
			firstParagraph:
				'We were able to sign you in, but not able to initialise your user account. This typically indicates that there has been an issue with creating your account during signup.',
			secondParagraph: 'Please contact us at',
			thirdParagraph: 'so we can help you resolve the issue.'
		}
	},

	NB: {
		loader: {
			title: 'Initialiserer konto',
			firstParagraph: 'Vennligst vent mens vi initialiserer kontoen din'
		},

		error: {
			title: 'Feil ved henting av konto',
			firstParagraph:
				'Du ble logget inn, men vi klarte ikke å initialisere kontoen din. Dette indikerer typisk at det har vært et problem med å opprette kontoen din under registrering.',
			secondParagraph: 'Vennligst kontakt oss på',
			thirdParagraph: 'så vi kan hjelpe deg å løse problemet.'
		}
	}
} as const;
type Translation = (typeof DICTIONARY)[keyof typeof DICTIONARY];
